import React, { useState } from 'react';
import { Nav, Button } from 'react-bootstrap';
import DomainsAndUrls from './DomainsAndUrls';
import BusinessContextAndAssets from './BusinessContextAndAssets';
import SectionApproval from './SectionApproval';
import ReRunModules from './ReRunModules';
import AICampaign from './AICampaign';

const SUBTABS = [
  { id: 'domains-urls',     title: 'Domains and URLs' },
  { id: 'run-modules',      title: 'Run Modules'      },
  { id: 'business-context', title: 'Business Context' },
  { id: 'section-approval', title: 'Section Approval' },
  { id: 'ai-campaign',      title: 'AI Campaign'      },
];

const DEFAULT_SUBTAB = 'domains-urls';

// Read the initial active subtab from ?tab=... so users can deep-link a
// particular subtab. Uses the History API directly (rather than
// useSearchParams) so the component renders fine in tests without a router
// wrapper. Falls back safely when window isn't available (SSR).
const readTabFromUrl = () => {
  if (typeof window === 'undefined') return DEFAULT_SUBTAB;
  try {
    const tab = new URLSearchParams(window.location.search).get('tab');
    return SUBTABS.some(t => t.id === tab) ? tab : DEFAULT_SUBTAB;
  } catch {
    return DEFAULT_SUBTAB;
  }
};

const writeTabToUrl = (tab) => {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    params.set('tab', tab);
    const next = `${window.location.pathname}?${params.toString()}${window.location.hash}`;
    window.history.replaceState({}, '', next);
  } catch {
    // Non-fatal — URL sync is a nice-to-have, not load-bearing.
  }
};

// Isolates render errors in any one subtab so they don't crash the whole
// AI Campaign Creation page. Without this, a thrown render in (e.g.)
// ReRunModules would unmount the Nav and the user would be stuck.
class SubtabErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[AICampaignCreation] subtab crashed:', error, info);
  }
  reset = () => this.setState({ hasError: false, error: null });
  render() {
    if (this.state.hasError) {
      return (
        <div className="p-4 text-center" role="alert">
          <h5 className="mb-2">Something went wrong loading this view</h5>
          <p className="text-muted small mb-3">{this.state.error?.message || 'Unknown error'}</p>
          <Button variant="primary" onClick={this.reset}>Try again</Button>
        </div>
      );
    }
    return this.props.children;
  }
}

const AICampaignCreation = ({ shop_domain, app_url, ViewedPage }) => {
  // useState's lazy initializer reads the URL exactly once on mount.
  const [activeSubtab, setActiveSubtabState] = useState(readTabFromUrl);

  const setActiveSubtab = (id) => {
    setActiveSubtabState(id);
    writeTabToUrl(id);
  };

  return (
    <div>
      <div className="dev-tabs-header mb-4">
        <Nav className="dev-tabs-header">
          {SUBTABS.map(t => (
            <Nav.Item key={t.id}>
              <Nav.Link
                className={activeSubtab === t.id ? 'active' : ''}
                onClick={() => setActiveSubtab(t.id)}
              >
                {t.title}
              </Nav.Link>
            </Nav.Item>
          ))}
        </Nav>
      </div>

      <div className="dev-content">
        <SubtabErrorBoundary key={activeSubtab}>
          {activeSubtab === 'domains-urls' && (
            <DomainsAndUrls
              ViewedPage={ViewedPage}
              shop_domain={shop_domain}
              app_url={app_url}
              isInSettingsContainer
              hideFetchDetails
            />
          )}
          {activeSubtab === 'run-modules' && (
            <ReRunModules
              shop_domain={shop_domain}
              app_url={app_url}
              excludeModules={['ai_campaign']}
            />
          )}
          {activeSubtab === 'business-context' && (
            <BusinessContextAndAssets
              ViewedPage={ViewedPage}
              shop_domain={shop_domain}
              app_url={app_url}
            />
          )}
          {activeSubtab === 'section-approval' && (
            <SectionApproval
              ViewedPage={ViewedPage}
              shop_domain={shop_domain}
              app_url={app_url}
            />
          )}
          {activeSubtab === 'ai-campaign' && (
            <AICampaign shop_domain={shop_domain} app_url={app_url} />
          )}
        </SubtabErrorBoundary>
      </div>
    </div>
  );
};

export default AICampaignCreation;